import { BasePdfTemplate } from './basePdfTemplate';
import type { ReceitaCompleta, ReceitaIngrediente } from '@/types/receita';
import type { PdfHeaderData } from '@/types/pdf';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const dificuldadeLabels: Record<string, string> = {
  facil: "Fácil",
  media: "Média",
  dificil: "Difícil",
};

export class ReceitaPdfTemplate extends BasePdfTemplate {
  public generate(receita: ReceitaCompleta, headerData: PdfHeaderData): void {
    const { receita: r, ingredientes } = receita;

    // Cabeçalho
    this.renderHeader(headerData);

    // Seções do PDF (ordem exata do dialog)
    this.renderInformacoesBasicas(r);
    this.addLine();
    this.currentY += 5;

    this.renderTempos(r);
    this.addLine();
    this.currentY += 5;

    this.renderIngredientesCards(ingredientes);
    this.addLine();
    this.currentY += 5;

    if (r.modo_preparo) {
      this.renderModoPreparo(r.modo_preparo);
      this.addLine();
      this.currentY += 5;
    }

    if (r.observacoes_internas || r.observacoes_entrega || r.modo_armazenamento || r.validade_dias) {
      this.renderObservacoes(r);
      this.addLine();
      this.currentY += 5;
    }

    this.renderMetadados(r);

    // Rodapé em todas as páginas
    const totalPages = this.doc.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      this.doc.setPage(i);
      this.renderFooter(i, totalPages, headerData.geradoPor);
    }
  }

  /**
   * Renderiza Informações Básicas (grid 2 colunas simulado)
   */
  private renderInformacoesBasicas(receita: any): void {
    this.checkPageBreak(40);

    // Título da seção
    this.doc.setFontSize(12);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text('Informações Básicas', this.margins.left, this.currentY);
    this.currentY += 8;

    // Grid 2 colunas (Nome, Categoria)
    const colWidth = (this.pageWidth - this.margins.left - this.margins.right) / 2;
    const col1X = this.margins.left;
    const col2X = this.margins.left + colWidth;

    // Linha 1: Nome | Categoria
    this.renderCampo('Nome', receita.nome || '—', col1X);
    const tempY = this.currentY;
    this.renderCampo('Categoria', receita.categoria || '—', col2X);
    this.currentY = Math.max(this.currentY, tempY);

    // Linha 2: Subcategoria | Dificuldade
    this.renderCampo('Subcategoria', receita.subcategoria || '—', col1X);
    const tempY2 = this.currentY;
    this.renderCampo('Dificuldade', receita.dificuldade ? dificuldadeLabels[receita.dificuldade] : '—', col2X);
    this.currentY = Math.max(this.currentY, tempY2);

    // Linha 3: Porção (apenas coluna 1)
    this.renderCampo('Porção', receita.porcao || '—', col1X);
    this.currentY += 5;
  }

  /**
   * Renderiza Tempos (grid 2 colunas simulado)
   */
  private renderTempos(receita: any): void {
    this.checkPageBreak(35);

    // Título da seção
    this.doc.setFontSize(12);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text('Tempos', this.margins.left, this.currentY);
    this.currentY += 8;

    const colWidth = (this.pageWidth - this.margins.left - this.margins.right) / 2;
    const col1X = this.margins.left;
    const col2X = this.margins.left + colWidth;

    // Linha 1: Preparo | Forno
    this.renderCampo('Preparo', receita.tempo_preparo_minutos ? `${receita.tempo_preparo_minutos} min` : '—', col1X);
    const tempY1 = this.currentY;
    this.renderCampo('Forno', receita.tempo_forno_minutos ? `${receita.tempo_forno_minutos} min` : '—', col2X);
    this.currentY = Math.max(this.currentY, tempY1);

    // Linha 2: Temperatura | Descanso
    this.renderCampo('Temperatura', receita.temperatura_forno_celsius ? `${receita.temperatura_forno_celsius}°C` : '—', col2X);
    const tempY2 = this.currentY;
    this.renderCampo('Descanso', receita.tempo_descanso_minutos ? `${receita.tempo_descanso_minutos} min` : '—', col1X);
    this.currentY = Math.max(this.currentY, tempY2);
    this.currentY += 5;
  }

  /**
   * Renderiza Ingredientes como cards com bordas pretas
   */
  private renderIngredientesCards(ingredientes: ReceitaIngrediente[]): void {
    this.checkPageBreak(20);

    // Título da seção
    this.doc.setFontSize(12);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text(`Ingredientes (${ingredientes.length})`, this.margins.left, this.currentY);
    this.currentY += 8;

    ingredientes.forEach((ing) => {
      // Calcular altura necessária do card
      const cardHeight = this.calculateCardHeight(ing);
      this.checkPageBreak(cardHeight + 5);

      const cardStartY = this.currentY;
      const cardPadding = 3;
      const cardWidth = this.pageWidth - this.margins.left - this.margins.right;

      // Desenhar retângulo com borda preta
      this.doc.setDrawColor(0, 0, 0);
      this.doc.setLineWidth(0.3);
      this.doc.rect(this.margins.left, cardStartY, cardWidth, cardHeight);

      // Conteúdo do card (grid 2 colunas simulado)
      let cardY = cardStartY + cardPadding + 4;
      const colWidth = cardWidth / 2;
      const col1X = this.margins.left + cardPadding;
      const col2X = this.margins.left + colWidth + cardPadding;

      // Linha 1: Produto | Quantidade
      this.doc.setFontSize(8);
      this.doc.setFont('helvetica', 'bold');
      this.doc.setTextColor(120, 120, 120);
      this.doc.text('Produto', col1X, cardY);
      this.doc.text('Quantidade', col2X, cardY);
      cardY += 4;

      this.doc.setFont('helvetica', 'normal');
      this.doc.setTextColor(0, 0, 0);
      const produtoNome = String(ing.produto_nome || 'Produto não informado').replace(/0+$/, '').trim() || 'Produto não informado';
      this.doc.text(produtoNome, col1X, cardY, { maxWidth: colWidth - cardPadding * 2 });

      let quantidade = '';
      if (ing.peso_gramas > 0) quantidade = `${ing.peso_gramas} g`;
      else if (ing.volume_ml > 0) quantidade = `${ing.volume_ml} ml`;
      else if (ing.quantidade_unidades > 0) quantidade = `${ing.quantidade_unidades} un.`;
      else quantidade = '—';
      this.doc.text(quantidade, col2X, cardY);
      cardY += 6;

      // Linha 2: Desperdício (se >0) | Tipo
      if (ing.desperdicio_percentual && ing.desperdicio_percentual > 0) {
        this.doc.setFont('helvetica', 'bold');
        this.doc.setTextColor(120, 120, 120);
        this.doc.text('Desperdício', col1X, cardY);
        cardY += 4;
        this.doc.setFont('helvetica', 'normal');
        this.doc.setTextColor(0, 0, 0);
        this.doc.text(`${ing.desperdicio_percentual}%`, col1X, cardY);
        cardY += 6;
      }

      if (ing.tipo_ingrediente) {
        const tipoY = cardY - (ing.desperdicio_percentual && ing.desperdicio_percentual > 0 ? 10 : 0);
        this.doc.setFont('helvetica', 'bold');
        this.doc.setTextColor(120, 120, 120);
        this.doc.text('Tipo', col2X, tipoY);
        this.doc.setFont('helvetica', 'normal');
        this.doc.setTextColor(0, 0, 0);
        this.doc.text(ing.tipo_ingrediente, col2X, tipoY + 4);
        if (!(ing.desperdicio_percentual && ing.desperdicio_percentual > 0)) {
          cardY += 10;
        }
      }

      // Linha 3: Ordem Preparo | Alérgenos (vermelho)
      if (ing.ordem_preparo) {
        this.doc.setFont('helvetica', 'bold');
        this.doc.setTextColor(120, 120, 120);
        this.doc.text('Ordem de Preparo', col1X, cardY);
        cardY += 4;
        this.doc.setFont('helvetica', 'normal');
        this.doc.setTextColor(0, 0, 0);
        this.doc.text(String(ing.ordem_preparo), col1X, cardY);
        cardY += 6;
      }

      if (ing.alergenos) {
        const alerY = cardY - (ing.ordem_preparo ? 10 : 0);
        this.doc.setFont('helvetica', 'bold');
        this.doc.setTextColor(120, 120, 120);
        this.doc.text('Alérgenos', col2X, alerY);
        this.doc.setFont('helvetica', 'normal');
        this.doc.setTextColor(220, 38, 38); // Vermelho (text-destructive)
        this.doc.text(ing.alergenos, col2X, alerY + 4);
        this.doc.setTextColor(0, 0, 0);
        if (!ing.ordem_preparo) {
          cardY += 10;
        }
      }

      // Linha 4: Substituível | Opcional
      if (ing.substituivel) {
        this.doc.setFont('helvetica', 'bold');
        this.doc.setTextColor(120, 120, 120);
        this.doc.text('Substituível', col1X, cardY);
        cardY += 4;
        this.doc.setFont('helvetica', 'normal');
        this.doc.setTextColor(0, 0, 0);
        this.doc.text(ing.substituivel, col1X, cardY);
        cardY += 6;
      }

      if (ing.opcional_receita) {
        const opcY = cardY - (ing.substituivel ? 10 : 0);
        this.doc.setFont('helvetica', 'bold');
        this.doc.setTextColor(120, 120, 120);
        this.doc.text('Opcional', col2X, opcY);
        this.doc.setFont('helvetica', 'normal');
        this.doc.setTextColor(0, 0, 0);
        this.doc.text(ing.opcional_receita, col2X, opcY + 4);
        if (!ing.substituivel) {
          cardY += 10;
        }
      }

      // Observações (span 2 colunas)
      if (ing.observacoes) {
        this.doc.setFont('helvetica', 'bold');
        this.doc.setTextColor(120, 120, 120);
        this.doc.text('Observações', col1X, cardY);
        cardY += 4;
        this.doc.setFont('helvetica', 'normal');
        this.doc.setTextColor(0, 0, 0);
        const obsLines = this.doc.splitTextToSize(ing.observacoes, cardWidth - cardPadding * 4);
        this.doc.text(obsLines, col1X, cardY);
        cardY += obsLines.length * 4;
      }

      this.currentY = cardStartY + cardHeight + 3;
    });

    this.currentY += 3;
  }

  /**
   * Calcula a altura necessária para um card de ingrediente
   */
  private calculateCardHeight(ing: ReceitaIngrediente): number {
    let height = 18; // Base: Produto + Quantidade

    if (ing.desperdicio_percentual && ing.desperdicio_percentual > 0) height += 10;
    if (ing.tipo_ingrediente) height += 10;
    if (ing.ordem_preparo) height += 10;
    if (ing.alergenos) height += 10;
    if (ing.substituivel) height += 10;
    if (ing.opcional_receita) height += 10;

    if (ing.observacoes) {
      const obsLines = this.doc.splitTextToSize(ing.observacoes, this.pageWidth - this.margins.left - this.margins.right - 12);
      height += obsLines.length * 4 + 8;
    }

    return height;
  }

  /**
   * Renderiza Modo de Preparo
   */
  private renderModoPreparo(modoPreparo: string): void {
    this.checkPageBreak(20);

    this.doc.setFontSize(12);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text('Modo de Preparo', this.margins.left, this.currentY);
    this.currentY += 8;

    this.doc.setFontSize(9);
    this.doc.setFont('helvetica', 'normal');
    const lines = modoPreparo.split('\n');
    lines.forEach((line) => {
      const wrappedLines = this.doc.splitTextToSize(line || ' ', this.pageWidth - this.margins.left - this.margins.right);
      wrappedLines.forEach((wrappedLine: string) => {
        this.checkPageBreak(5);
        this.doc.text(wrappedLine, this.margins.left, this.currentY);
        this.currentY += 5;
      });
    });

    this.currentY += 3;
  }

  /**
   * Renderiza Observações
   */
  private renderObservacoes(receita: any): void {
    this.checkPageBreak(30);

    this.doc.setFontSize(12);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text('Observações', this.margins.left, this.currentY);
    this.currentY += 8;

    if (receita.modo_armazenamento) {
      this.renderCampo('Armazenamento', receita.modo_armazenamento, this.margins.left);
    }

    if (receita.validade_dias) {
      this.renderCampo('Validade', `${receita.validade_dias} dias`, this.margins.left);
    }

    if (receita.observacoes_entrega) {
      this.renderCampo('Observações de Entrega', receita.observacoes_entrega, this.margins.left);
    }

    if (receita.observacoes_internas) {
      this.renderCampo('Observações Internas', receita.observacoes_internas, this.margins.left);
    }

    this.currentY += 3;
  }

  /**
   * Renderiza Metadados (Criada em)
   */
  private renderMetadados(receita: any): void {
    this.checkPageBreak(10);

    this.doc.setFontSize(8);
    this.doc.setFont('helvetica', 'normal');
    this.doc.setTextColor(120, 120, 120);

    const criadaEm = format(new Date(receita.created_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
    this.doc.text(`Criada em: ${criadaEm}`, this.margins.left, this.currentY);
    this.currentY += 5;
  }

  /**
   * Método auxiliar para renderizar label + valor
   */
  private renderCampo(label: string, valor: string, x: number): void {
    this.checkPageBreak(10);

    this.doc.setFontSize(8);
    this.doc.setFont('helvetica', 'bold');
    this.doc.setTextColor(120, 120, 120);
    this.doc.text(label, x, this.currentY);
    this.currentY += 4;

    this.doc.setFont('helvetica', 'normal');
    this.doc.setTextColor(0, 0, 0);
    const maxWidth = (this.pageWidth - this.margins.left - this.margins.right) / 2 - 5;
    const lines = this.doc.splitTextToSize(valor, maxWidth);
    lines.forEach((line: string) => {
      this.doc.text(line, x, this.currentY);
      this.currentY += 4;
    });
    this.currentY += 2;
  }
}
